"use client";

import {
  Box,
  Button,
  Grid,
  TextField,
  Paper,
  FormControl,
  Select,
  MenuItem,
  FormLabel,
  FormHelperText,
  Typography,
  Autocomplete,
  Alert,
  Chip,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Gender } from "../../../../utils";
import { useFetchData } from "../../../../api/api/FetchData";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import AddBloodTypeDialog from "./AddBloodTypeDialog";
import useAdd from "../../../../api/useAdd";
import useFetchObjects from "../../../../api/useFetchObjects";
import ClientSearchDialog from "../../../../Components/GeneralComponents/ClientSearchDialog";

// Button styling
const buttonStyle = {
  textTransform: "capitalize",
  backgroundColor: "#071952",
};

// Helper function to get current local date/time in a "datetime-local" accepted format.
const getCurrentDateTimeLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = ("0" + (now.getMonth() + 1)).slice(-2);
  const day = ("0" + now.getDate()).slice(-2);
  const hours = ("0" + now.getHours()).slice(-2);
  const minutes = ("0" + now.getMinutes()).slice(-2);
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const NewPatient = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;
  const { bloodTypeData } = useFetchData();

  const [unitCode, setUnitCode] = useState("");

  // Fetch users for the follow-up field.
  const { data: usersData } = useFetchObjects(["users"], `users/`, token);

  useEffect(() => {
    const generateCode = () => {
      let code;
      do {
        code = Math.floor(1000 + Math.random() * 9000).toString(); // Generate a 4-digit code
      } while (codeExists(code)); // Ensure uniqueness
      setUnitCode(code);
    };

    generateCode(); // Generate code on component mount
  }, []);

  const [openBloodTypeDialog, setOpenBloodTypeDialog] = useState(false);
  const handleOpenBloodTypeDialog = () => setOpenBloodTypeDialog(true);
  const handleCloseBloodTypeDialog = () => setOpenBloodTypeDialog(false);

  // Client search dialog state
  const [openClientSearchDialog, setOpenClientSearchDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientLinked, setClientLinked] = useState(false);

  // Form data remains independent until a client is manually selected.
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    age: "",
    blood_type: "",
    phone: "",
    passport: "",
    address: "",
    dosia_file_id: "",
    registration_date: getCurrentDateTimeLocal(),
    followUpBy: "",
  });
  const [errors, setErrors] = useState({});

  // ---------------------------
  // Phone Number Formatter for both Searching & Submitting
  // ---------------------------
  const formatPhoneForSearch = (phone) => {
    if (phone.startsWith("+")) return phone;
    if (phone.startsWith("0")) return phone.substring(1);
    return phone;
  };

  // ---------------------------
  // Validation Function
  // ---------------------------
  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    if (!formData.firstName) {
      newErrors.firstName = t("reception.firstNameRequired");
      isValid = false;
    }

    if (formData.dosia_file_id && !/^\d+$/.test(formData.dosia_file_id)) {
      newErrors.dosia_file_id = t("reception.invalidDosiaFileId");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // ---------------------------
  // Client Search Functionality
  // ---------------------------
  const [filterValue, setFilterValue] = useState("");
  const timerRef = useRef(null);

  useEffect(() => {
    // When a phone number is entered, debounce before setting the filter value.
    const phoneTrim = formData.phone.trim();
    // Only trigger if the phone is not empty and has valid numeric format (optionally starting with +)
    const isValidPhone = phoneTrim !== "" && /^[+]?\d+$/.test(phoneTrim);
    if (isValidPhone) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const formattedPhone = formatPhoneForSearch(phoneTrim);
        setFilterValue(formattedPhone);
      }, 300);
    } else {
      // Clear the filter value when phone is invalid or empty.
      setFilterValue("");
    }
  }, [formData.phone]);

  // Fetch matching client(s) using the phone number.
  const {
    data: clientData,
    isLoading: isLoadingClient,
    isError: isErrorClient,
    refetch: refetchClient,
  } = useFetchObjects(
    ["search-client"],
    `search-client/?contact=${filterValue}`,
    token,
    {
      enabled: false,
    }
  );

  useEffect(() => {
    if (filterValue.trim() !== "") {
      refetchClient();
    }
  }, [filterValue, refetchClient]);

  // ---------------------------
  // Update form with selected client info only when the client is clicked.
  // ---------------------------
  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setClientLinked(true);

    // Split client's full_name into first and last names.
    const fullName = client.full_name || "";
    const nameParts = fullName.split(" ");
    const firstNameFromClient = nameParts[0] || "";
    const lastNameFromClient = nameParts.slice(1).join(" ") || "";

    // Get the follow-up person from the client
    const followUpPerson = client.follow_up_by;
    const followUpId = followUpPerson ? followUpPerson.id : "";

    // Update form with the client's info. Also update the phone field with client's contact.
    setFormData((prev) => ({
      ...prev,
      firstName: firstNameFromClient,
      lastName: lastNameFromClient,
      phone: client.contact || "",
      address: client.address || "",
      age: client.age || "",
      gender:
        client.gender === 1 ? "Male" : client.gender === 2 ? "Female" : "",
      followUpBy: followUpId,
    }));
  };

  // ---------------------------
  // Form Submission & Input Handling
  // ---------------------------
  const handleSubmit = (event) => {
    event.preventDefault();

    if (validateForm()) {
      const patientData = {
        firstname: formData.firstName,
        lastname: formData.lastName || null,
        age: formData.age ? formData.age : null,
        gender: formData.gender,
        blood_type: formData.blood_type || null,
        // Use the same formatter for submission.
        phone: formatPhoneForSearch(formData.phone.trim()),
        passport: formData.passport,
        address: formData.address || null,
        dosia_file_id: formData.dosia_file_id || null,
        registration_date: formData.registration_date,
        follow_up_by: formData.followUpBy || null,
        // Include client ID if linked
        linked_client_id: selectedClient?.id || null,
      };

      handleAdd(patientData);
    }
  };

  // Updated input change handling: when the phone changes we clear auto-populated client info
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "phone") {
      // Only clear the filter value without clearing manually entered data
      setFormData((prev) => ({
        ...prev,
        phone: value,
      }));
      setFilterValue("");
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle client search dialog
  const handleOpenClientSearch = () => setOpenClientSearchDialog(true);
  const handleCloseClientSearch = () => setOpenClientSearchDialog(false);

  const { handleAdd, resetForm } = useAdd("patients", token);

  // ---------------------------
  // Reset Form and Clear Found Client on Successful Submission
  // ---------------------------
  useEffect(() => {
    if (resetForm) {
      setFormData({
        firstName: "",
        lastName: "",
        gender: "",
        age: "",
        blood_type: "",
        phone: "",
        passport: "",
        address: "",
        dosia_file_id: "",
        registration_date: getCurrentDateTimeLocal(),
        followUpBy: "",
      });
      setFilterValue("");
    }
  }, [resetForm]);

  // Dummy function to check if the code already exists
  const codeExists = (code) => {
    // Replace this logic with your own to check against existing codes
    // For demonstration, let's say no code exists
    return false;
  };

  return (
    <Grid container maxWidth="xl" mt={3} mb={5}>
      <Grid item xs={12} md={10} margin="0px auto">
        <Paper sx={{ boxShadow: "none", width: "100%" }}>
          {/* Display matching client options only as selectable items */}
          {!isLoadingClient &&
            !isErrorClient &&
            clientData &&
            clientData.length > 0 && (
              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: "#e0f7fa",
                  borderRadius: "8px",
                }}
                elevation={2}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("call_center.clientView")}
                </Typography>
                {clientData.map((client) => (
                  <Box
                    key={client.id}
                    sx={{
                      p: 1,
                      mb: 1,
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      cursor: "pointer",
                    }}
                    onClick={() => handleClientSelect(client)}
                  >
                    <Typography>
                      {t("call_center.fullName")}: {client.full_name}
                    </Typography>
                    <Typography>
                      {t("call_center.address")}:{" "}
                      {client.address ||
                        t("call_center.noAddress", "No Address Provided")}
                    </Typography>
                    <Typography>
                      {t("call_center.contact")}: {client.contact}
                    </Typography>
                    <Typography>
                      {t("call_center.additionalNumber")}: {client.contact_2}
                    </Typography>
                    <Typography>
                      {t("call_center.followUpBy")}:{" "}
                      {client?.display_user
                        ? `${client.display_user.first_name} ${client.display_user.last_name}`
                        : t("call_center.notAssigned")}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            )}

          {/* Client Search Section */}
          <Box
            sx={{ p: 2, mb: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">
                {t("reception.patientRegistration", "Patient Registration")}
              </Typography>
              <Button
                variant="outlined"
                onClick={handleOpenClientSearch}
                sx={{ textTransform: "capitalize" }}
              >
                {t("reception.searchExistingClient", "Search Existing Client")}
              </Button>
            </Box>

            {clientLinked && selectedClient && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Client Linked Successfully!
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    label={`Name: ${selectedClient.full_name}`}
                    size="small"
                  />
                  <Chip
                    label={`Phone: ${selectedClient.contact}`}
                    size="small"
                  />
                  <Chip
                    label={`Follow-up: ${selectedClient.follow_up_by?.first_name} ${selectedClient.follow_up_by?.last_name}`}
                    size="small"
                  />
                  <Chip
                    label={`Status: ${selectedClient.status?.name || "N/A"}`}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Follow-up person has been automatically selected from the
                  client record. You can change it if needed. When this patient
                  is registered, the client status will be automatically updated
                  to "Done".
                </Typography>
              </Alert>
            )}
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            padding="20px"
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.firstName")}</FormLabel>
                <TextField
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.lastName")}</FormLabel>
                <TextField
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.phone")}</FormLabel>
                <TextField
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.passport")}</FormLabel>
                <TextField
                  name="passport"
                  value={formData.passport}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.age")}</FormLabel>
                <TextField
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                  error={!!errors.age}
                  helperText={errors.age}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <FormControl
                  size="small"
                  fullWidth
                  error={!!errors.gender}
                  required
                >
                  <FormLabel>{t("reception.gender")}</FormLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                  >
                    {Gender.map((item, index) => (
                      <MenuItem value={item.id} key={index}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.gender && (
                    <FormHelperText>{errors.gender}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <FormControl size="small" fullWidth>
                  <FormLabel>{t("reception.blood_type")}</FormLabel>
                  <Select
                    name="blood_type"
                    value={formData.blood_type}
                    onChange={handleInputChange}
                  >
                    <MenuItem onClick={handleOpenBloodTypeDialog}>
                      {t("reception.addNew")}
                    </MenuItem>
                    {bloodTypeData.map((item, index) => (
                      <MenuItem value={item.id} key={index}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.dosiaFileId")}</FormLabel>
                <TextField
                  name="dosia_file_id"
                  value={unitCode}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                  error={!!errors.dosia_file_id}
                  helperText={errors.dosia_file_id || t("reception.optional")}
                />
              </Grid>
              <Grid item xs={12}>
                <FormLabel>{t("reception.address")}</FormLabel>
                <TextField
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder={t("reception.enterAddress")}
                  error={!!errors.address}
                  helperText={errors.address || t("reception.optional")}
                />
              </Grid>
              {/* Registration Date Field */}
              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.registrationDate")}</FormLabel>
                <TextField
                  type="datetime-local"
                  name="registration_date"
                  value={formData.registration_date}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              {/* Autocomplete for Follow-Up User */}
              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.followUpBy")}</FormLabel>
                <Autocomplete
                  options={usersData || []}
                  getOptionLabel={(option) =>
                    `${option.first_name} ${option.last_name}`
                  }
                  value={
                    (usersData &&
                      formData.followUpBy &&
                      usersData.find((u) => u.id === formData.followUpBy)) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      followUpBy: newValue ? newValue.id : "",
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      placeholder={t("reception.selectUser")}
                      helperText={
                        clientLinked && selectedClient?.follow_up_by
                          ? "Auto-selected from client record"
                          : ""
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end">
                  <Button type="submit" variant="contained" style={buttonStyle}>
                    {t("reception.submit")}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <AddBloodTypeDialog
            open={openBloodTypeDialog}
            onClose={handleCloseBloodTypeDialog}
            token={token}
          />

          <ClientSearchDialog
            open={openClientSearchDialog}
            onClose={handleCloseClientSearch}
            onSelectClient={handleClientSelect}
            token={token}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default NewPatient;
